import type { ServicePageConfig } from "../../types";
import {
  getPackagePricingRows,
  PACKAGE_PRICE_HINT_SV,
  PACKAGE_PRICE_HINT_EN,
} from "../../package-pricing";
import { getCityMunicipalityList } from "../../cities";

const stockholmKommunerText = getCityMunicipalityList("stockholm");

export const sollentunaPages: ServicePageConfig[] = [
  {
    slug: "bortforsling-sollentuna",
    serviceCategory: "bortforsling",
    city: "stockholm",

    meta: {
      title: "Bortforsling Sollentuna – Snabb Hämtning | Ungdomskraft",
      description:
        "Boka bortforsling i Sollentuna. Vi hämtar möbler, vitvaror och skräp i Tureberg, Edsberg, Helenelund, Häggvik, Norrviken och Rotebro. Fast pris, ofta samma dag.",
      h1: "Bortforsling Sollentuna",
    },

    hero: {
      heading: "Bortforsling i Sollentuna",
      intro:
        "Boka bortforsling i Sollentuna – vi hämtar möbler, vitvaror och skrymmande föremål i hela kommunen, från villakvarteren i Edsberg och Norrviken till lägenheterna i Tureberg och Helenelund. Snabb bokning, fast pris och ofta tillgängliga redan samma dag. Över 50 000 slutförda uppdrag i Stockholmsregionen.",
      heroImage: "/hero-bortforsling.webp",
      heroImageAlt:
        "Ungdomskraft hämtar möbler vid bortforsling i Sollentuna – ung medarbetare lastar skåpbil",
    },

    itemsSection: {
      heading: "Saker vi bortforslar i Sollentuna",
      items: [
        {
          name: "Återvinning",
          description: "Glas, metall, papper och annat återvinningsbart",
          image: "/recycling-bin.webp",
          alt: "Återvinning som hämtas vid bortforsling i Sollentuna och körs till Smedby ÅVC",
        },
        {
          name: "Kartonger",
          description: "Flyttkartonger och wellpappkartonger",
          image: "/box.webp",
          alt: "Flyttkartonger som forslas bort från villa i Edsberg, Sollentuna",
        },
        {
          name: "Möbler",
          description: "Vardagsrum, sovrum, kontor och andra hemmöbler",
          image: "/furniture.webp",
          alt: "Soffor och hemmöbler vid bortforsling i Tureberg, Sollentuna",
        },
        {
          name: "Elektronik",
          description:
            "TV-apparater, datorer, ljudsystem och annan elektronik",
          image: "/electronics.webp",
          alt: "Elektronik och TV-apparater som bortforslas i Helenelund, Sollentuna",
        },
        {
          name: "Vitvaror",
          description:
            "Kylskåp, tvättmaskiner, torktumlare och andra vitvaror",
          image: "/appliances.webp",
          alt: "Kylskåp och vitvaror som hämtas vid bortforsling i Norrviken, Sollentuna",
        },
        {
          name: "Träningsutrustning",
          description:
            "Löpband, cyklar, vikter och annan träningsutrustning",
          image: "/fitness-equipment.webp",
          alt: "Löpband och träningsutrustning vid bortforsling i Rotebro, Sollentuna",
        },
      ],
    },

    seoContent: {
      mainHeading: "Bortforsling i Sollentuna – snabbt, smidigt och ansvarsfullt",
      mainText:
        "Sollentuna är en kommun med stor variation – från villaområdena i Edsberg, Norrviken och Viby till de tätare lägenhetskvarteren i Tureberg och Helenelund. Den blandningen gör bortforsling till olika utmaningar beroende på var du bor: i villan handlar det ofta om en full garageuppfart eller ett källarförråd som ska tömmas, medan det i flerbostadshusen kring Malmvägen och Sollentuna centrum snarare handlar om trånga trapphus och hissar. Med Ungdomskraft slipper du allt krångel oavsett vilket. Vi skickar unga, starka medarbetare med skåpbil direkt till din adress.\n\nVårt team hanterar hela kedjan: vi bär ut föremålen från din lägenhet, villa eller ditt förråd, lastar i bilen och kör till närmaste återvinningscentral. För Sollentuna är de närmaste SÖRAB-anläggningarna Smedby ÅVC strax norrut och Hagby ÅVC i Täby – men vi anpassar oss alltid efter var i kommunen du befinner dig. Du behöver inte tänka på sortering, parkering eller tunga lyft. Vi tar hand om allt.\n\nTack vare att E4:an går rakt genom kommunen, med trafikplatser vid Häggvik och Helenelund, och Norrortsleden ansluter i norr, tar vi oss snabbt mellan kommundelarna. Det gör att vi ofta kan vara på plats i Sollentuna redan samma dag du bokar. Våra medarbetare är vana vid både de smala villagatorna i Töjnan och Skälby och de större bostadsgårdarna i Tureberg – och har utrustningen som krävs: bärbälten, skyddsfiltar och lastramper som skyddar både dina ägodelar och trapphuset under hela processen.",
      preparationHeading: "Förbered inför din bortforsling i Sollentuna",
      preparationText:
        "Du behöver egentligen inte göra mycket alls – våra ungdomar sköter det tunga jobbet. Men vill du snabba på processen och hålla nere kostnaden finns det några enkla saker du kan göra:\n\nRensa en fri väg (ungefär 1 meter bred) från entrén till föremålen som ska bort. Bor du i villa i Edsberg eller Norrviken är det smidigt om vi kan backa in på uppfarten – meddela oss om grinden eller garageporten behöver öppnas. Bor du i lägenhet i Tureberg eller Helenelund, kontrollera om det finns en lastzon, innergård eller gästparkering nära porten som vi kan använda för lastning. Det sparar tid jämfört med att leta plats på gatan.\n\nSamla gärna sakerna på ett ställe om möjligt, till exempel i hallen, garaget eller förrådet. Du behöver inte demontera möbler – det ingår i vår tjänst om det behövs. Farligt avfall som färgrester, batterier och kemikalier kan vi tyvärr inte ta med, men sådant lämnar du gratis som Sollentunabo på SÖRAB:s återvinningscentraler, till exempel Smedby ÅVC.",
      additionalSections: [
        {
          heading: "Kommundelar och områden vi täcker i Sollentuna",
          content:
            "Vi utför bortforsling i hela Sollentuna kommun – från de stora stadsutvecklingsområdena till de etablerade villakvarteren. Oavsett om du bor i en nybyggd lägenhet i Silverdal, en villa i Edsberg, ett radhus i Viby eller en etta vid Sollentuna centrum kan vi vara på plats, ofta redan samma dag.\n\nI de centrala delarna – Tureberg med Sollentuna centrum, Malmvägen och områdena runt kommunhuset – har vi stor vana vid lägenhetshämtningar med hiss och trapphus. I Helenelund, som gränsar mot Kista och Solna, hanterar vi både de äldre villorna kring Edsviken och de nyare kvarteren i Silverdal och Tegelhagen. I Edsberg når vi allt från Edsbacka och Edsbergs centrum till den växande stadsdelen kring Väsjön.\n\nVi är minst lika snabba i kommunens norra och västra delar: Häggvik med handelsplatsen, Skälby, Norrviken, Rotebro, Viby, Vaxmora och Sjöberg. Tack vare närheten till E4:an, Norrortsleden och kommunens fem pendeltågsstationer – Helenelund, Sollentuna, Häggvik, Norrviken och Rotebro – planerar vi alltid rutten i förväg så att du slipper vänta. Inga extra avgifter för någon kommundel.",
        },
        {
          heading: "Återvinning och miljöansvar i Sollentuna",
          content:
            "En viktig del av vår bortforsling i Sollentuna är att allt hanteras på ett miljövänligt sätt. Vi sorterar materialet och kör det till godkända återvinningscentraler i området. Sollentuna är en av delägarkommunerna i SÖRAB, och de stationer vi använder mest för uppdrag i kommunen är:\n\nSmedby ÅVC – den närmaste SÖRAB-anläggningen, strax norr om kommungränsen, lättillgänglig via E4:an för uppdrag i Rotebro, Norrviken och Häggvik. Hagby ÅVC i Täby – ett bra alternativ för hämtningar i östra Sollentuna och Edsberg. Vi väljer alltid den station som ligger närmast din adress för att hålla restiden – och därmed kostnaden – nere.\n\nDet som kan återanvändas skänker vi vidare till organisationer som Myrorna och Stadsmissionen. På så sätt bidrar din bortforsling till cirkulär ekonomi – sakerna får ett nytt liv istället för att hamna på tippen.\n\nOm sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
        },
        {
          heading: "Varför boka bortforsling i Sollentuna med Ungdomskraft?",
          listItems: [
            {
              label: "Lokal kunskap –",
              text: "Vi känner Sollentunas kommundelar, från villagatorna i Töjnan och Skälby till bostadsgårdarna i Tureberg och Helenelund, och vet vilka SÖRAB-stationer som ligger närmast. Det gör att vi planerar effektiva rutter och håller nere kostnaden för dig.",
            },
            {
              label: "Snabb tillgänglighet –",
              text: "Vi har skåpbilar och personal i hela Stockholmsregionen och E4:an går rakt genom Sollentuna. Ofta kan vi vara på plats redan samma dag du bokar – även på kvällar och helger.",
            },
            {
              label: "Fast pris utan överraskningar –",
              text: "Du ser priset direkt vid bokning. Inga dolda avgifter, inga tilläggsavgifter för någon kommundel i Sollentuna, och inga offertförfrågningar. Boka på två minuter.",
            },
            {
              label: "Försäkrat och tryggt –",
              text: "Alla uppdrag är försäkrade genom Trygg Hansa. Ditt trapphus, din villauppfart, dina väggar och dina ägodelar är skyddade under hela processen.",
            },
            {
              label: "Miljövänlig hantering –",
              text: "Vi sorterar och återvinner allt vi kan via SÖRAB:s återvinningscentraler. Det som går att donera skänks vidare. Du kan ha rätt till RUT-avdrag för transport till återbruk.",
            },
          ],
        },
      ],
    },

    faq: [
      {
        question: "Vad kostar bortforsling i Sollentuna?",
        answer:
          "Priset beror på volym, vikt och tid. Vår minsta kategori (Extra Liten) startar på 249 kr i grundavgift, och ett genomsnittligt uppdrag kostar runt 499–2 495 kr beroende på mängden. Du ser det exakta priset direkt vid bokning – inga dolda avgifter och inga tillägg för någon kommundel i Sollentuna.",
      },
      {
        question: "Kan jag få RUT-avdrag på bortforsling i Sollentuna?",
        answer:
          "Om sakerna transporteras till en secondhand-butik eller välgörenhetsorganisation för återanvändning kan du ha rätt till RUT-avdrag på arbetskostnaden. Vanlig bortforsling där allt körs till återvinningsstation ger inte rätt till avdrag. Avdraget hanteras direkt på fakturan.",
      },
      {
        question: "Hur snabbt kan ni komma för bortforsling i Sollentuna?",
        answer:
          "Vi har skåpbilar och personal i hela Stockholmsregionen och E4:an går rakt genom Sollentuna, så vi tar oss snabbt mellan kommundelarna. Ofta kan vi vara på plats redan samma dag, och vid akuta behov ibland inom 60 minuter. Boka online och välj den tid som passar dig.",
      },
      {
        question: "Ingår det en skåpbil vid bortforsling i Sollentuna?",
        answer:
          "Ja, vi kommer alltid med skåpbil och personal. Du behöver inte ordna eget fordon – vi sköter allt från bärning till transport och bortkörning till närmaste återvinningscentral.",
      },
      {
        question: "Kan ni bära ner saker från lägenhet i trappa i Sollentuna?",
        answer:
          "Absolut. Vi är vana vid både trapphus och hissar i flerbostadshusen kring Tureberg, Malmvägen och Helenelund. Vi bär ner möbler, vitvaror och annat oavsett våning – inga extra avgifter för trappor.",
      },
      {
        question: "Hämtar ni från villa och garage i Sollentuna?",
        answer:
          "Ja, en stor del av våra uppdrag i Sollentuna är villahämtningar i Edsberg, Norrviken, Viby och Töjnan. Vi backar in på uppfarten där det går och tömmer garage, källare och förråd. Meddela oss bara om en grind eller garageport behöver öppnas.",
      },
      {
        question: "Kan ni demontera möbler innan bortforsling?",
        answer:
          "Ja, vi demonterar möbler på plats om det behövs för att få ut dem genom dörrar eller nedför trappor. Det ingår i tjänsten utan extra kostnad.",
      },
      {
        question: "Är bortforslingen i Sollentuna försäkrad?",
        answer:
          "Ja, alla uppdrag är försäkrade genom Trygg Hansa. Det innebär att din fastighet, ditt trapphus, din villauppfart och dina ägodelar är skyddade under hela processen – från lyft till transport.",
      },
      {
        question: "Vart kör ni avfallet efter bortforsling i Sollentuna?",
        answer:
          "Vi sorterar allt material och kör det till närmaste godkända återvinningscentral. För Sollentuna är det oftast Smedby ÅVC eller Hagby ÅVC i Täby, båda SÖRAB-anläggningar. Det som kan återanvändas skänks till Myrorna eller Stadsmissionen.",
      },
      {
        question: "Kan jag boka bortforsling i Sollentuna på helgen?",
        answer:
          "Ja, vi erbjuder bortforsling alla dagar i veckan, inklusive lördagar och söndagar. Välj det datum och den tid som passar dig bäst direkt i vår bokning.",
      },
      {
        question: "Kan ni hämta saker från källare eller förråd i Sollentuna?",
        answer:
          "Ja, vi hämtar föremål från alla utrymmen – källare, förråd, vindsutrymmen och garage. Vi är vana vid både villaförråden i Edsberg och lägenhetsförråden i Tureberg och Helenelund.",
      },
      {
        question: "Kan ni göra flera stopp vid bortforsling i Sollentuna?",
        answer:
          "Ja, du kan lägga till flera hämtadresser i samma bokning. Det är praktiskt om du till exempel behöver hämta saker från både din lägenhet vid Sollentuna centrum och ditt förråd på en annan adress i kommunen.",
      },
      {
        question: "Vilka kommundelar i Sollentuna täcker ni?",
        answer:
          "Vi utför bortforsling i hela Sollentuna – Tureberg, Edsberg (inklusive Väsjön), Helenelund (inklusive Silverdal), Häggvik, Norrviken, Rotebro, Viby, Vaxmora, Sjöberg och övriga områden. Inga extra avgifter för någon kommundel.",
      },
      {
        question: "Tar ni hand om farligt avfall vid bortforsling i Sollentuna?",
        answer:
          "Vi kan tyvärr inte transportera farligt avfall som färg, kemikalier, batterier eller asbest. Sådant lämnar du gratis som Sollentunabo på SÖRAB:s återvinningscentraler, till exempel Smedby ÅVC.",
      },
      {
        question: "Vilka kommuner runt Sollentuna utför ni bortforsling i?",
        answer: `Vi täcker hela Stockholmsregionen, inklusive följande kommuner: ${stockholmKommunerText}. Inga extra avgifter för kranskommuner.`,
      },
    ],

    priceHint: PACKAGE_PRICE_HINT_SV.bortforsling,

    pricingRows: getPackagePricingRows("bortforsling", "sv"),

    dynamicHeadings: {
      threeSteps: "Boka din bortforsling i Sollentuna i 3 enkla steg",
      step2Text:
        "Våra ungdomar i Sollentuna kommer till dig och hämtar det du vill ha bortforslat. Vi hanterar tunga lyft, trånga trapphus, smala hissar och fulla villagarage – du behöver inte lyfta ett finger.",
      ctaHeading: "Boka bortforsling i Sollentuna idag",
      ctaText:
        "Fyll i formuläret och få pris direkt – inga offertförfrågningar. Smidig bortforsling i hela Sollentuna kommun.",
    },

    en: {
      slug: "junk-removal-sollentuna",
      meta: {
        title: "Junk Removal Sollentuna – Fast Pickup | Ungdomskraft",
        description:
          "Book junk removal in Sollentuna. We pick up furniture, appliances and bulky items in Tureberg, Edsberg, Helenelund, Häggvik, Norrviken and Rotebro. Fixed price, often same day.",
        h1: "Junk Removal Sollentuna",
      },
      hero: {
        heading: "Junk Removal in Sollentuna",
        intro:
          "Book junk removal in Sollentuna – we collect furniture, appliances and bulky items across the whole municipality, from the villa neighbourhoods of Edsberg and Norrviken to the apartments in Tureberg and Helenelund. Quick booking, fixed price and often available the same day. Over 50,000 completed jobs in the Stockholm region.",
        heroImageAlt:
          "Ungdomskraft collecting furniture during junk removal in Sollentuna – young worker loading a van",
      },
      itemsSection: {
        heading: "Items we remove in Sollentuna",
        items: [
          {
            name: "Recycling",
            description: "Glass, metal, paper and other recyclables",
            image: "/recycling-bin.webp",
            alt: "Recyclables collected during junk removal in Sollentuna and driven to Smedby recycling centre",
          },
          {
            name: "Boxes",
            description: "Moving boxes and cardboard boxes",
            image: "/box.webp",
            alt: "Moving boxes removed from a villa in Edsberg, Sollentuna",
          },
          {
            name: "Furniture",
            description: "Living room, bedroom, office and other home furniture",
            image: "/furniture.webp",
            alt: "Sofas and home furniture during junk removal in Tureberg, Sollentuna",
          },
          {
            name: "Electronics",
            description: "TVs, computers, sound systems and other electronics",
            image: "/electronics.webp",
            alt: "Electronics and TVs removed in Helenelund, Sollentuna",
          },
          {
            name: "Appliances",
            description: "Fridges, washing machines, dryers and other appliances",
            image: "/appliances.webp",
            alt: "Fridges and appliances collected during junk removal in Norrviken, Sollentuna",
          },
          {
            name: "Fitness Equipment",
            description: "Treadmills, bikes, weights and other exercise equipment",
            image: "/fitness-equipment.webp",
            alt: "Treadmill and fitness equipment during junk removal in Rotebro, Sollentuna",
          },
        ],
      },
      seoContent: {
        mainHeading: "Junk removal in Sollentuna – fast, hassle-free and responsible",
        mainText:
          "Sollentuna is a municipality of great variety – from the villa areas of Edsberg, Norrviken and Viby to the denser apartment blocks of Tureberg and Helenelund. That mix makes junk removal a different challenge depending on where you live: in the villas it is often a full garage driveway or a basement storage room to be cleared, while in the apartment buildings around Malmvägen and Sollentuna centrum it is more about narrow stairwells and elevators. With Ungdomskraft, you skip the hassle either way. We send young, strong workers with a van directly to your address.\n\nOur team handles the entire chain: we carry items out of your apartment, villa or storage unit, load them into the van and drive to the nearest recycling centre. For Sollentuna, the closest SÖRAB facilities are Smedby recycling centre just to the north and Hagby recycling centre in Täby – but we always adapt to where in the municipality you are located. You don't need to worry about sorting, parking or heavy lifting. We take care of everything.\n\nBecause the E4 motorway runs straight through the municipality, with interchanges at Häggvik and Helenelund, and Norrortsleden connecting to the north, we move quickly between districts. This means we can often be on site in Sollentuna the same day you book. Our workers are used to both the narrow villa streets of Töjnan and Skälby and the larger residential courtyards of Tureberg – and have the right equipment: carrying belts, protective blankets and loading ramps that protect both your belongings and the stairwell throughout the process.",
        preparationHeading: "Preparing for your junk removal in Sollentuna",
        preparationText:
          "You don't really need to do much at all – our young workers handle the heavy work. But if you want to speed things up and keep the cost down, there are a few simple things you can do:\n\nClear a path (about 1 metre wide) from the entrance to the items that need to go. If you live in a villa in Edsberg or Norrviken, it helps if we can reverse onto the driveway – let us know if a gate or garage door needs opening. If you live in an apartment in Tureberg or Helenelund, check whether there is a loading zone, courtyard or visitor parking near the entrance we can use. It saves time compared to searching for a spot on the street.\n\nGather the items in one place if possible, for example in the hallway, garage or storage unit. You don't need to disassemble furniture – we include that in our service if needed. We cannot take hazardous waste such as paint, batteries and chemicals, but as a Sollentuna resident you can drop those off for free at SÖRAB's recycling centres, such as Smedby.",
        additionalSections: [
          {
            heading: "Districts and areas we cover in Sollentuna",
            content:
              "We perform junk removal across the entire municipality of Sollentuna – from the large new development areas to the established villa neighbourhoods. Whether you live in a new apartment in Silverdal, a villa in Edsberg, a townhouse in Viby or a studio near Sollentuna centrum, we can be on site, often the same day.\n\nIn the central parts – Tureberg with Sollentuna centrum, Malmvägen and the areas around the municipal hall – we are highly experienced with apartment pickups via elevator and stairwell. In Helenelund, which borders Kista and Solna, we handle both the older villas around Edsviken and the newer blocks in Silverdal and Tegelhagen. In Edsberg we reach everything from Edsbacka and Edsbergs centrum to the growing district around Väsjön.\n\nWe are just as quick in the northern and western parts of the municipality: Häggvik with its retail park, Skälby, Norrviken, Rotebro, Viby, Vaxmora and Sjöberg. Thanks to the proximity of the E4, Norrortsleden and the municipality's five commuter-rail stations – Helenelund, Sollentuna, Häggvik, Norrviken and Rotebro – we always plan the route in advance so you don't have to wait. No extra charges for any district.",
          },
          {
            heading: "Recycling and environmental responsibility in Sollentuna",
            content:
              "An important part of our junk removal in Sollentuna is that everything is handled in an environmentally responsible way. We sort the materials and drive them to approved recycling centres in the area. Sollentuna is one of the part-owner municipalities of SÖRAB, and the stations we use most for jobs in the municipality are:\n\nSmedby recycling centre – the closest SÖRAB facility, just north of the municipal border, easily reached via the E4 for jobs in Rotebro, Norrviken and Häggvik. Hagby recycling centre in Täby – a good option for pickups in eastern Sollentuna and Edsberg. We always choose the station nearest your address to keep travel time – and therefore the cost – down.\n\nItems that can be reused are donated to organisations like Myrorna and Stadsmissionen. This way, your junk removal contributes to a circular economy – items get a new life instead of ending up in landfill.\n\nIf the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
          },
          {
            heading: "Why book junk removal in Sollentuna with Ungdomskraft?",
            listItems: [
              {
                label: "Local knowledge –",
                text: "We know Sollentuna's districts, from the villa streets of Töjnan and Skälby to the residential courtyards of Tureberg and Helenelund, and which SÖRAB stations are closest. This lets us plan efficient routes and keep the cost down for you.",
              },
              {
                label: "Fast availability –",
                text: "We have vans and staff across the Stockholm region and the E4 runs straight through Sollentuna. We can often be on site the same day you book – including evenings and weekends.",
              },
              {
                label: "Fixed price with no surprises –",
                text: "You see the price immediately when booking. No hidden fees, no surcharges for any district in Sollentuna, and no quote requests. Book in two minutes.",
              },
              {
                label: "Insured and safe –",
                text: "All jobs are insured through Trygg Hansa. Your stairwell, your driveway, your walls and your belongings are protected throughout the entire process.",
              },
              {
                label: "Eco-friendly handling –",
                text: "We sort and recycle everything we can via SÖRAB's recycling centres. Items suitable for donation are passed on. You may be entitled to a RUT tax deduction for transport to reuse.",
              },
            ],
          },
        ],
      },
      faq: [
        {
          question: "How much does junk removal in Sollentuna cost?",
          answer:
            "The price depends on volume, weight and time. Our smallest category (Extra Liten) starts at 249 kr base fare, and an average job costs around 499–1,699 kr depending on the amount. You see the exact price immediately when booking – no hidden fees and no surcharges for any district in Sollentuna.",
        },
        {
          question: "Can I get a RUT tax deduction on junk removal in Sollentuna?",
          answer:
            "If the items are transported to a charity shop or organisation for reuse, you may be eligible for the Swedish RUT tax deduction on the labour cost. Standard junk removal where everything goes to a recycling station does not qualify. The deduction is handled directly on the invoice.",
        },
        {
          question: "How quickly can you come for junk removal in Sollentuna?",
          answer:
            "We have vans and staff across the Stockholm region and the E4 runs straight through Sollentuna, so we move quickly between districts. We can often be on site the same day, and for urgent needs sometimes within 60 minutes. Book online and choose the time that suits you.",
        },
        {
          question: "Is a van included with junk removal in Sollentuna?",
          answer:
            "Yes, we always come with a van and crew. You don't need to arrange your own vehicle – we handle everything from carrying to transport and disposal at the nearest recycling centre.",
        },
        {
          question: "Can you carry items down from an apartment via stairs in Sollentuna?",
          answer:
            "Absolutely. We are used to both stairwells and elevators in the apartment buildings around Tureberg, Malmvägen and Helenelund. We carry furniture, appliances and other items regardless of floor – no extra charges for stairs.",
        },
        {
          question: "Do you collect from villas and garages in Sollentuna?",
          answer:
            "Yes, a large share of our jobs in Sollentuna are villa pickups in Edsberg, Norrviken, Viby and Töjnan. We reverse onto the driveway where possible and clear garages, basements and storage units. Just let us know if a gate or garage door needs opening.",
        },
        {
          question: "Can you disassemble furniture before removal?",
          answer:
            "Yes, we disassemble furniture on site if needed to get it through doors or down stairs. This is included in the service at no extra cost.",
        },
        {
          question: "Is junk removal in Sollentuna insured?",
          answer:
            "Yes, all jobs are insured through Trygg Hansa. This means your property, stairwell, driveway and belongings are protected throughout the entire process – from lifting to transport.",
        },
        {
          question: "Where do you take the waste after junk removal in Sollentuna?",
          answer:
            "We sort all materials and drive them to the nearest approved recycling centre. For Sollentuna that is usually Smedby recycling centre or Hagby in Täby, both SÖRAB facilities. Items that can be reused are donated to Myrorna or Stadsmissionen.",
        },
        {
          question: "Can I book junk removal in Sollentuna on weekends?",
          answer:
            "Yes, we offer junk removal every day of the week, including Saturdays and Sundays. Choose the date and time that works best for you directly in our booking.",
        },
        {
          question: "Can you collect items from basements or storage units in Sollentuna?",
          answer:
            "Yes, we pick up items from all spaces – basements, storage units, attics and garages. We are used to both the villa storage rooms in Edsberg and the apartment storage units in Tureberg and Helenelund.",
        },
        {
          question: "Can you make multiple stops during junk removal in Sollentuna?",
          answer:
            "Yes, you can add multiple pickup addresses in the same booking. This is practical if you need to collect items from both your apartment near Sollentuna centrum and a storage unit at another address in the municipality, for example.",
        },
        {
          question: "Which districts in Sollentuna do you cover?",
          answer:
            "We perform junk removal across all of Sollentuna – Tureberg, Edsberg (including Väsjön), Helenelund (including Silverdal), Häggvik, Norrviken, Rotebro, Viby, Vaxmora, Sjöberg and all other areas. No extra charges for any district.",
        },
        {
          question: "Do you handle hazardous waste during junk removal in Sollentuna?",
          answer:
            "Unfortunately, we cannot transport hazardous waste such as paint, chemicals, batteries or asbestos. As a Sollentuna resident you can drop those off for free at SÖRAB's recycling centres, such as Smedby.",
        },
        {
          question: "Which municipalities around Sollentuna do you serve?",
          answer:
            "We cover the entire Stockholm region, including Botkyrka, Danderyd, Ekerö, Haninge, Huddinge, Järfälla, Lidingö, Nacka, Norrtälje, Nykvarn, Nynäshamn, Salem, Sigtuna, Sollentuna, Solna, Stockholm, Sundbyberg, Södertälje, Tyresö, Täby, Upplands Väsby, Upplands-Bro, Vallentuna, Vaxholm, Värmdö and Österåker. No extra charges for surrounding municipalities.",
        },
      ],
      priceHint: PACKAGE_PRICE_HINT_EN.bortforsling,
      pricingRows: getPackagePricingRows("bortforsling", "en"),
      dynamicHeadings: {
        threeSteps: "Book your junk removal in Sollentuna in 3 easy steps",
        step2Text:
          "Our young workers in Sollentuna come to you and collect whatever you want removed. We handle heavy lifting, tight staircases, narrow elevators and full villa garages – you don't have to lift a finger.",
        ctaHeading: "Book junk removal in Sollentuna today",
        ctaText:
          "Fill in the form and get an instant price – no quote requests. Easy junk removal across the whole municipality of Sollentuna.",
      },
    },
  },
];
